/**
 * Backoffice CRM: the reply composer on the inbox's thread page (#1524, #1541, customer_inbox_spec.md § Sending).
 *
 * A TipTap editor (bold, italic, underline, strike, links, lists, quotes), the thread's shared draft saved as you
 * type, saved replies to insert or to create from what you wrote, your sign-off, a preview of exactly what goes out
 * (previewInboxReply), and a send with a few seconds to undo. The server rebuilds the HTML from an allowlist and picks
 * the recipient: the composer only says what to write. "Ask for a rating" (#2163) adds the server's rate-us line
 * under the sign-off; the tick is remembered in this browser. The From picker (#2381) sends as hello@ or a person's
 * alias, also remembered in this browser; the server accepts only the addresses in SENDERS (inbox_reply_compose.js).
 *
 * TipTap is imported when a thread opens, not with the module, so the CRM's Node tests (no DOM) never load it.
 */
import { escapeHTML } from "./familyDetailsPanel.js";
import { formatWhen } from "./inboxThreadExtras.js";

const DRAFT_SAVE_MS = 1500;
const UNDO_SECONDS = 5;
const RATING_ASK_KEY = "crm.inbox.askForRating";
const FROM_KEY = "crm.inbox.from";
/** The addresses a reply may come from, as the server's SENDERS lists them. The first is the default. */
export const SENDER_ADDRESSES = [
  "hello@weeknightbiteapp.com", "anton@weeknightbiteapp.com", "hannah@weeknightbiteapp.com", "lukas@weeknightbiteapp.com"
];

let ctx = null;
let editor = null;
let threadId = null;
let thread = null;
let myUid = null;
let onSent = null;
let saveTimer = null;
let lastSavedHtml = "";
let savedReplies = [];
let sendTimer = null;
/** Whether the composer writes a new message (#2660) instead of a reply, and the subject it was given. */
let newMessage = false;
/** The customer's other threads waiting on us that this send may also answer (#2660), and the ticked ones. */
let alsoCandidates = { threadId: null, list: [] };
let alsoTicked = new Set();

/** Why this thread can't be answered, or null. */
export function replyBlockedReason(t) {
  const email = t && t.customer && t.customer.email;
  if (t && (t.source === "app_store" || t.source === "google_play")) return null; // answered in public, on the store
  if (t && t.automated) return "Sent by software: there is nobody to reply to.";
  if (t && t.source === "app_feedback" && !email) return "They didn't leave an address and weren't signed in, so there's no way to reply.";
  if (!email || /@weeknightbiteapp\.com$/i.test(email)) return "This conversation has no customer address to reply to.";
  return null;
}

/** "Lukas started this draft 3 min ago", for a draft another admin wrote; "" for your own or none. */
export function draftNoticeText(draft, uid, now = new Date()) {
  if (!draft || !draft.updated_by || draft.updated_by.uid === uid) return "";
  const who = String(draft.updated_by.name || draft.updated_by.email || "Another admin").split(/\s+/)[0];
  return `${who} started this draft ${ago(draft.updated_at, now)}. It's shared: you can finish it.`;
}

function ago(iso, now) {
  const min = Math.round((now.getTime() - new Date(iso).getTime()) / 60000);
  if (!(min >= 1)) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  return h < 24 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
}

/** The saved replies menu: insert one, or delete it. */
export function savedRepliesMenuHTML(list) {
  if (!list.length) {
    return `<p class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No saved replies yet. Write one, then "Save as a saved reply".</p>`;
  }
  return list.map((r) => `<div class="flex items-center gap-1 px-1">
    <button type="button" data-saved-insert="${escapeHTML(r.id)}" class="min-w-0 flex-1 truncate rounded-md px-2 py-1.5 text-left text-sm text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-white/10">${escapeHTML(r.title)}</button>
    <button type="button" data-saved-delete="${escapeHTML(r.id)}" aria-label="Delete the saved reply ${escapeHTML(r.title)}" class="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"><svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="size-4"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" /></svg></button>
  </div>`).join("");
}

const TOOL = "rounded-md px-2 py-1 text-sm font-semibold text-gray-700 hover:bg-gray-200 aria-pressed:bg-gray-900 aria-pressed:text-white dark:text-gray-200 dark:hover:bg-white/10 dark:aria-pressed:bg-white dark:aria-pressed:text-gray-900";
const SMALL = "rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 disabled:opacity-50 dark:bg-white/10 dark:text-white dark:shadow-none dark:inset-ring-white/5 dark:hover:bg-white/20";
const PRIMARY = "rounded-md bg-paprika-600 px-3 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-paprika-500 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paprika-600";

function composerHTML(t) {
  const to = t.customer.name ? `${t.customer.name} <${t.customer.email}>` : t.customer.email;
  return `<div data-composer>
    <div class="flex flex-wrap items-baseline justify-between gap-2">
      <p class="text-sm font-semibold text-gray-900 dark:text-white"><span data-composer-verb>Reply to</span> <span class="font-normal text-gray-600 dark:text-gray-300">${escapeHTML(to)}</span>
        <label class="ml-2 font-normal text-gray-500 dark:text-gray-400">from
          <select data-composer-from aria-label="Send from" class="ml-1 rounded-md disabled:opacity-50 bg-white py-0.5 pr-7 pl-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:outline-paprika-600 dark:bg-white/5 dark:text-white dark:outline-white/10">
            ${SENDER_ADDRESSES.map((a) => `<option value="${escapeHTML(a)}">${escapeHTML(a)}</option>`).join("")}
          </select>
        </label>
      </p>
      <p data-composer-status class="text-xs text-gray-500 dark:text-gray-400"></p>
    </div>
    <p data-composer-notice class="mt-1 text-xs font-medium text-amber-700 dark:text-amber-400" hidden></p>
    <div data-composer-edit>
      <div class="mt-2 flex flex-wrap items-center gap-2">
        <div class="flex items-center gap-0.5 rounded-md border border-gray-300 bg-white px-1 py-1 dark:border-white/10 dark:bg-white/5" role="group" aria-label="Kind of message">
          <button type="button" data-composer-kind="reply" aria-pressed="true" title="Answer this conversation, with its last message quoted" class="${TOOL}">Reply</button>
          <button type="button" data-composer-kind="new" aria-pressed="false" title="A new email with its own subject and nothing quoted; their answer comes back to this conversation" class="${TOOL}">New message</button>
        </div>
        <input data-composer-subject type="text" maxlength="200" placeholder="Subject" aria-label="Subject of the new message" class="min-w-0 flex-1 rounded-md bg-white px-2 py-1 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:outline-paprika-600 dark:bg-white/5 dark:text-white dark:outline-white/10" hidden />
      </div>
      <div class="mt-2 flex flex-wrap items-center gap-0.5 rounded-t-md border border-b-0 border-gray-300 bg-white px-1 py-1 dark:border-white/10 dark:bg-white/5" role="toolbar" aria-label="Formatting">
        <button type="button" data-cmd="bold" aria-pressed="false" title="Bold (⌘B)" class="${TOOL}"><b>B</b></button>
        <button type="button" data-cmd="italic" aria-pressed="false" title="Italic (⌘I)" class="${TOOL}"><i>I</i></button>
        <button type="button" data-cmd="underline" aria-pressed="false" title="Underline (⌘U)" class="${TOOL}"><u>U</u></button>
        <button type="button" data-cmd="link" aria-pressed="false" title="Link" class="${TOOL}">Link</button>
        <button type="button" data-cmd="bulletList" aria-pressed="false" title="Bulleted list" class="${TOOL}">• List</button>
        <button type="button" data-cmd="orderedList" aria-pressed="false" title="Numbered list" class="${TOOL}">1. List</button>
        <button type="button" data-cmd="blockquote" aria-pressed="false" title="Quote" class="${TOOL}">“ Quote</button>
        <span class="mx-1 h-5 w-px bg-gray-200 dark:bg-white/10" aria-hidden="true"></span>
        <div class="relative">
          <button type="button" data-composer-saved-toggle aria-expanded="false" class="${TOOL}">Saved replies ▾</button>
          <div data-composer-saved-menu class="absolute bottom-full left-0 z-10 mb-1 max-h-72 w-72 overflow-y-auto rounded-md bg-white py-1 shadow-lg outline-1 outline-black/5 dark:bg-gray-800 dark:outline-white/10" hidden></div>
        </div>
      </div>
      <div data-composer-link-row class="flex items-center gap-2 border border-b-0 border-gray-300 bg-white px-2 py-1.5 dark:border-white/10 dark:bg-white/5" hidden>
        <input data-composer-link-input type="url" placeholder="https://…" aria-label="Link address" class="min-w-0 flex-1 rounded-md bg-white px-2 py-1 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:outline-paprika-600 dark:bg-white/5 dark:text-white dark:outline-white/10" />
        <button type="button" data-composer-link-apply class="${SMALL}">Apply</button>
        <button type="button" data-composer-link-remove class="${SMALL}">Remove link</button>
      </div>
      <div data-composer-editor class="rounded-b-md border border-gray-300 bg-white px-3 py-2 text-sm/6 text-gray-900 dark:border-white/10 dark:bg-gray-900/40 dark:text-gray-100"></div>
      <div class="mt-2 flex items-start justify-between gap-3">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
          <button type="button" data-composer-signoff-toggle class="text-left text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" title="Change your sign-off">Signed <span data-composer-signoff class="whitespace-pre-line font-medium text-gray-700 dark:text-gray-300"></span> · edit</button>
          <label class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400" title="Adds a P.S. under your sign-off asking for an App Store or Google Play rating">
            <input type="checkbox" data-composer-rating class="size-3.5 accent-paprika-600" />
            Ask for a rating
          </label>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <button type="button" data-composer-save-reply class="${SMALL}" title="Keep this text to insert into other replies">Save as a saved reply</button>
          <button type="button" data-composer-preview class="${PRIMARY}" title="See exactly what goes out (⌘↩)">Preview &amp; send</button>
        </div>
      </div>
      <div data-composer-also-box hidden></div>
      <div data-composer-signoff-row class="mt-2 flex items-start gap-2" hidden>
        <textarea data-composer-signoff-input rows="2" aria-label="Your sign-off" class="min-w-0 flex-1 rounded-md bg-white px-2 py-1 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:outline-paprika-600 dark:bg-white/5 dark:text-white dark:outline-white/10"></textarea>
        <button type="button" data-composer-signoff-save class="${SMALL}">Save sign-off</button>
      </div>
      <div data-composer-title-row class="mt-2 flex items-center gap-2" hidden>
        <input data-composer-title-input type="text" maxlength="120" placeholder="Name it, e.g. Mealime link gone" aria-label="Saved reply name" class="min-w-0 flex-1 rounded-md bg-white px-2 py-1 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:outline-paprika-600 dark:bg-white/5 dark:text-white dark:outline-white/10" />
        <button type="button" data-composer-title-save class="${SMALL}">Save</button>
      </div>
    </div>
    <div data-composer-preview-pane hidden>
      <dl class="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt class="text-gray-500 dark:text-gray-400">From</dt><dd data-preview-from class="text-gray-900 dark:text-white"></dd>
        <dt class="text-gray-500 dark:text-gray-400">To</dt><dd data-preview-to class="text-gray-900 dark:text-white"></dd>
        <dt class="text-gray-500 dark:text-gray-400">Subject</dt><dd data-preview-subject class="text-gray-900 dark:text-white"></dd>
      </dl>
      <p data-preview-also class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-200" hidden></p>
      <div data-preview-frame class="mt-2"></div>
      <div class="mt-3 flex items-center justify-end gap-2">
        <span data-composer-countdown class="mr-auto text-sm font-medium text-gray-700 dark:text-gray-200"></span>
        <button type="button" data-composer-back class="${SMALL}">Back to editing</button>
        <button type="button" data-composer-send class="${PRIMARY}">Send</button>
      </div>
    </div>
  </div>`;
}

/** Mounts the composer for a thread, or hides it when the thread can't be answered. */
export async function mountComposer(options) {
  const container = document.getElementById("inboxComposer");
  if (!container) return;
  if (editor && threadId === options.thread.id) return; // the same thread, refreshed: keep what is being written
  await destroyComposer();
  ctx = options.ctx;
  thread = options.thread;
  threadId = thread.id;
  myUid = options.myUid;
  onSent = options.onSent;
  newMessage = false;
  alsoTicked = new Set();
  if (replyBlockedReason(thread)) {
    container.hidden = true;
    return;
  }
  container.hidden = false;
  if (thread.source === "app_store" || thread.source === "google_play") {
    mountStoreComposer(container);
    return;
  }
  container.innerHTML = composerHTML(thread);

  const [{ Editor }, { StarterKit }, { Placeholder }] = await Promise.all([
    import("@tiptap/core"), import("@tiptap/starter-kit"), import("@tiptap/extensions")
  ]);
  if (threadId !== options.thread.id) return; // another thread opened while TipTap loaded
  const first = (thread.customer.name || "").split(/\s+/)[0] || "them";
  editor = new Editor({
    element: container.querySelector("[data-composer-editor]"),
    extensions: [
      StarterKit.configure({
        heading: false, codeBlock: false, horizontalRule: false, code: false,
        link: { openOnClick: false, autolink: true, defaultProtocol: "https", protocols: ["mailto"] }
      }),
      Placeholder.configure({ placeholder: `Write to ${first}…` })
    ],
    editorProps: {
      attributes: {
        class: "min-h-28 focus:outline-none [&_a]:text-paprika-700 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_.is-editor-empty:first-child::before]:pointer-events-none [&_.is-editor-empty:first-child::before]:float-left [&_.is-editor-empty:first-child::before]:h-0 [&_.is-editor-empty:first-child::before]:text-gray-400 [&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
        "aria-label": "Your reply"
      },
      handleKeyDown: (view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
          showPreview();
          return true;
        }
        return false;
      }
    },
    onUpdate: () => { scheduleDraftSave(); updateToolbar(); },
    onSelectionUpdate: updateToolbar
  });
  bind(container);
  drawAlso();
  await Promise.all([loadDraft(), loadSavedReplies(), loadSignoff()]);
}

/** Saves a pending draft, then removes the editor. */
export async function destroyComposer() {
  if (sendTimer) {
    clearInterval(sendTimer);
    sendTimer = null;
  }
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
    await saveDraft();
  }
  if (editor) editor.destroy();
  editor = null;
  storeMode = false;
  threadId = null;
  thread = null;
  const container = document.getElementById("inboxComposer");
  if (container) {
    container.hidden = true;
    container.innerHTML = "";
  }
}

function call(name, data) {
  const { functions, httpsCallable } = ctx || {};
  return httpsCallable(functions, name)(data).then((res) => res.data || {});
}

function $(selector) {
  return document.querySelector(`#inboxComposer ${selector}`);
}

function setStatus(text) {
  const el = $("[data-composer-status]");
  if (el) el.textContent = text;
}

function bind(container) {
  container.querySelectorAll("[data-composer-kind]").forEach((btn) => btn.addEventListener("click", () => setKind(btn.dataset.composerKind === "new")));
  container.querySelectorAll("[data-cmd]").forEach((btn) => btn.addEventListener("click", () => runCommand(btn.dataset.cmd)));
  $("[data-composer-link-apply]")?.addEventListener("click", applyLink);
  $("[data-composer-link-input]")?.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } });
  $("[data-composer-link-remove]")?.addEventListener("click", () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    $("[data-composer-link-row]").hidden = true;
  });
  $("[data-composer-saved-toggle]")?.addEventListener("click", () => {
    const menu = $("[data-composer-saved-menu]");
    menu.hidden = !menu.hidden;
    $("[data-composer-saved-toggle]").setAttribute("aria-expanded", String(!menu.hidden));
  });
  $("[data-composer-saved-menu]")?.addEventListener("click", onSavedMenuClick);
  $("[data-composer-save-reply]")?.addEventListener("click", () => {
    const row = $("[data-composer-title-row]");
    row.hidden = !row.hidden;
    if (!row.hidden) $("[data-composer-title-input]").focus();
  });
  $("[data-composer-title-save]")?.addEventListener("click", saveAsSavedReply);
  $("[data-composer-signoff-toggle]")?.addEventListener("click", () => {
    const row = $("[data-composer-signoff-row]");
    row.hidden = !row.hidden;
    if (!row.hidden) $("[data-composer-signoff-input]").focus();
  });
  $("[data-composer-signoff-save]")?.addEventListener("click", saveSignoff);
  const rating = $("[data-composer-rating]");
  if (rating) {
    rating.checked = rememberedRatingAsk();
    rating.addEventListener("change", () => rememberRatingAsk(rating.checked));
  }
  const from = $("[data-composer-from]");
  if (from) {
    from.value = rememberedFrom();
    from.addEventListener("change", () => rememberFrom(from.value));
  }
  $("[data-composer-preview]")?.addEventListener("click", showPreview);
  $("[data-composer-back]")?.addEventListener("click", backToEditing);
  $("[data-composer-send]")?.addEventListener("click", startSend);
}

function runCommand(cmd) {
  if (!editor) return;
  const chain = editor.chain().focus();
  if (cmd === "bold") chain.toggleBold().run();
  else if (cmd === "italic") chain.toggleItalic().run();
  else if (cmd === "underline") chain.toggleUnderline().run();
  else if (cmd === "bulletList") chain.toggleBulletList().run();
  else if (cmd === "orderedList") chain.toggleOrderedList().run();
  else if (cmd === "blockquote") chain.toggleBlockquote().run();
  else if (cmd === "link") {
    const row = $("[data-composer-link-row]");
    row.hidden = !row.hidden;
    if (!row.hidden) {
      const input = $("[data-composer-link-input]");
      input.value = editor.getAttributes("link").href || "";
      input.focus();
    }
  }
}

function applyLink() {
  const raw = $("[data-composer-link-input]").value.trim();
  if (!raw) return;
  const href = /^(https?:|mailto:)/i.test(raw) ? raw : (/^[^\s@]+@[^\s@]+$/.test(raw) ? `mailto:${raw}` : `https://${raw}`);
  const chain = editor.chain().focus().extendMarkRange("link");
  if (editor.state.selection.empty && !editor.isActive("link")) chain.insertContent(`<a href="${escapeHTML(href)}">${escapeHTML(raw)}</a>`).run();
  else chain.setLink({ href }).run();
  $("[data-composer-link-row]").hidden = true;
}

function updateToolbar() {
  if (!editor) return;
  const active = { bold: "bold", italic: "italic", underline: "underline", link: "link", bulletList: "bulletList", orderedList: "orderedList", blockquote: "blockquote" };
  document.querySelectorAll("#inboxComposer [data-cmd]").forEach((btn) => {
    btn.setAttribute("aria-pressed", String(editor.isActive(active[btn.dataset.cmd])));
  });
}

/** Whether "Ask for a rating" is ticked (#2163). */
function askForRating() {
  const el = $("[data-composer-rating]");
  return Boolean(el && el.checked);
}

function rememberedRatingAsk() {
  try { return localStorage.getItem(RATING_ASK_KEY) === "1"; } catch { return false; }
}

function rememberRatingAsk(on) {
  try { localStorage.setItem(RATING_ASK_KEY, on ? "1" : "0"); } catch { /* private mode: not remembered */ }
}

/** The address picked in From (#2381), hello@ when the picker is missing. */
function fromAddress() {
  const el = $("[data-composer-from]");
  return el && SENDER_ADDRESSES.includes(el.value) ? el.value : SENDER_ADDRESSES[0];
}

/** What this browser last picked, if it is still one of ours. */
export function rememberedFrom(storage = globalThis.localStorage) {
  try {
    const saved = storage.getItem(FROM_KEY);
    return SENDER_ADDRESSES.includes(saved) ? saved : SENDER_ADDRESSES[0];
  } catch { return SENDER_ADDRESSES[0]; }
}

function rememberFrom(address) {
  try { localStorage.setItem(FROM_KEY, address); } catch { /* private mode: not remembered */ }
}

/**
 * The customer's other conversations waiting on us, as tick boxes under the editor (#2660): "Also count this as the
 * answer to". Nothing is ticked by default, so a reply never closes another conversation on its own.
 */
export function alsoAnswersHTML(list, ticked = new Set()) {
  if (!list || !list.length) return "";
  return `<fieldset class="mt-2"><legend class="text-xs font-medium text-gray-700 dark:text-gray-300">Also count this as the answer to:</legend>` +
    `<div class="mt-1 flex flex-col gap-1">${list.map((t) => `<label class="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300"><input type="checkbox" data-composer-also="${escapeHTML(t.id)}"${ticked.has(t.id) ? " checked" : ""} class="size-3.5 accent-paprika-600" /><span class="truncate">${escapeHTML(t.subject || "(no subject)")}</span><span class="shrink-0 text-gray-400">· since ${escapeHTML(formatWhen(t.last_message_at))}</span></label>`).join("")}</div>` +
    `<p class="mt-1 text-xs text-gray-500 dark:text-gray-400">They move to Waiting; nothing is sent to them.</p></fieldset>`;
}

/** Tells the composer which of this customer's other threads wait on us, once the page has read them (#2660). */
export function setAlsoAnswerable(forThreadId, list) {
  alsoCandidates = { threadId: forThreadId, list: Array.isArray(list) ? list : [] };
  const ids = new Set(alsoCandidates.list.map((t) => t.id));
  alsoTicked = new Set([...alsoTicked].filter((id) => ids.has(id)));
  drawAlso();
}

function alsoSelected() {
  if (alsoCandidates.threadId !== threadId) return [];
  return alsoCandidates.list.filter((t) => alsoTicked.has(t.id));
}

function alsoPayload() {
  const ids = alsoSelected().map((t) => t.id);
  return ids.length ? { alsoAnswers: ids } : {};
}

function drawAlso() {
  const box = $("[data-composer-also-box]");
  if (!box) return;
  const list = alsoCandidates.threadId === threadId ? alsoCandidates.list : [];
  box.innerHTML = alsoAnswersHTML(list, alsoTicked);
  box.hidden = !list.length;
  box.querySelectorAll("[data-composer-also]").forEach((input) => input.addEventListener("change", () => {
    if (input.checked) alsoTicked.add(input.dataset.composerAlso);
    else alsoTicked.delete(input.dataset.composerAlso);
  }));
}

/** Switches between a reply and a new message (#2660): the subject box and the header's words follow. */
function setKind(isNew) {
  newMessage = isNew;
  $("[data-composer-kind=\"reply\"]")?.setAttribute("aria-pressed", String(!isNew));
  $("[data-composer-kind=\"new\"]")?.setAttribute("aria-pressed", String(isNew));
  const subject = $("[data-composer-subject]");
  if (subject) {
    subject.hidden = !isNew;
    if (isNew) subject.focus();
  }
  const verb = $("[data-composer-verb]");
  if (verb) verb.textContent = isNew ? "New message to" : "Reply to";
}

/** What the server is told of the kind: nothing for a reply, the flag and the subject for a new message. */
function messageKind() {
  return newMessage ? { newMessage: true, subject: ($("[data-composer-subject]")?.value || "").trim() } : {};
}

/** Why a new message can't be previewed yet (a reply is never blocked here), or null. */
export function newMessageBlockedReason(subject) {
  return String(subject || "").trim() ? null : "Give the new message a subject.";
}

function currentHtml() {
  return editor && !editor.isEmpty ? editor.getHTML() : "";
}

function scheduleDraftSave() {
  if (saveTimer) clearTimeout(saveTimer);
  setStatus("Editing…");
  saveTimer = setTimeout(() => {
    saveTimer = null;
    saveDraft();
  }, DRAFT_SAVE_MS);
}

async function saveDraft() {
  const html = currentHtml();
  if (html === lastSavedHtml || !threadId) return;
  const id = threadId;
  try {
    await call("saveInboxDraft", { threadId: id, html });
    lastSavedHtml = html;
    if (threadId === id) setStatus(html ? "Draft saved" : "");
  } catch (err) {
    if (threadId === id) setStatus("Draft not saved");
    console.warn("⚠️ saveInboxDraft failed:", err);
  }
}

async function loadDraft() {
  const { db, doc, getDoc } = ctx;
  const id = threadId;
  try {
    const snap = await getDoc(doc(db, "inbox_threads", id, "drafts", "current"));
    if (threadId !== id || !snap.exists()) return;
    const draft = snap.data();
    if (editor && editor.isEmpty && draft.html) {
      editor.commands.setContent(draft.html, { emitUpdate: false });
      lastSavedHtml = editor.getHTML();
    }
    const notice = draftNoticeText(draft, myUid);
    const el = $("[data-composer-notice]");
    if (el && notice) {
      el.textContent = notice;
      el.hidden = false;
    }
  } catch (err) {
    console.warn("⚠️ Draft unreadable:", err);
  }
}

async function loadSavedReplies() {
  const { db, collection, query, orderBy, getDocs } = ctx;
  try {
    const snap = await getDocs(query(collection(db, "inbox_saved_replies"), orderBy("title")));
    savedReplies = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn("⚠️ Saved replies unreadable:", err);
    savedReplies = [];
  }
  const menu = $("[data-composer-saved-menu]");
  if (menu) menu.innerHTML = savedRepliesMenuHTML(savedReplies);
}

async function onSavedMenuClick(e) {
  const insert = e.target.closest?.("[data-saved-insert]");
  if (insert) {
    const reply = savedReplies.find((r) => r.id === insert.dataset.savedInsert);
    if (reply && editor) editor.chain().focus().insertContent(reply.html).run();
    $("[data-composer-saved-menu]").hidden = true;
    return;
  }
  const del = e.target.closest?.("[data-saved-delete]");
  if (del) {
    const reply = savedReplies.find((r) => r.id === del.dataset.savedDelete);
    if (!reply || !window.confirm(`Delete the saved reply "${reply.title}" for both of you?`)) return;
    try {
      await call("deleteInboxSavedReply", { id: reply.id });
      ctx.showToast?.("Saved reply deleted.", "success");
      await loadSavedReplies();
    } catch (err) {
      ctx.showToast?.(`Couldn't delete it: ${err.message}`, "error");
    }
  }
}

async function saveAsSavedReply() {
  const title = $("[data-composer-title-input]").value.trim();
  const html = currentHtml();
  if (!title || !html) {
    ctx.showToast?.(!html ? "Write the reply first." : "Give it a name.", "warning");
    return;
  }
  try {
    await call("saveInboxSavedReply", { title, html });
    $("[data-composer-title-row]").hidden = true;
    $("[data-composer-title-input]").value = "";
    ctx.showToast?.(`Saved "${title}".`, "success");
    await loadSavedReplies();
  } catch (err) {
    ctx.showToast?.(`Couldn't save it: ${err.message}`, "error");
  }
}

async function loadSignoff() {
  const { db, doc, getDoc } = ctx;
  let value = "";
  try {
    const snap = await getDoc(doc(db, "inbox_admins", myUid));
    value = snap.exists() ? snap.data().signoff || "" : "";
  } catch (err) {
    console.warn("⚠️ Sign-off unreadable:", err);
  }
  showSignoff(value || defaultSignoffFor(ctx.currentUserName?.() || ""));
}

function defaultSignoffFor(name) {
  const first = String(name).trim().split(/\s+/)[0] || "WeeknightBite";
  return `Best,\n${first}`;
}

function showSignoff(value) {
  const el = $("[data-composer-signoff]");
  if (el) el.textContent = value.replace(/\n/g, " ");
  const input = $("[data-composer-signoff-input]");
  if (input) input.value = value;
}

async function saveSignoff() {
  const value = $("[data-composer-signoff-input]").value;
  try {
    const res = await call("setInboxSignoff", { signoff: value });
    showSignoff(res.signoff || "");
    $("[data-composer-signoff-row]").hidden = true;
    ctx.showToast?.("Sign-off saved: it's used for your replies only.", "success");
  } catch (err) {
    ctx.showToast?.(`Couldn't save the sign-off: ${err.message}`, "error");
  }
}

async function showPreview() {
  const html = currentHtml();
  if (!html) {
    ctx.showToast?.("Write the reply first.", "warning");
    return;
  }
  const blocked = newMessage ? newMessageBlockedReason($("[data-composer-subject]")?.value) : null;
  if (blocked) {
    ctx.showToast?.(blocked, "warning");
    $("[data-composer-subject]")?.focus();
    return;
  }
  const btn = $("[data-composer-preview]");
  if (btn) btn.disabled = true;
  try {
    const preview = await call("previewInboxReply", { threadId, html, askForRating: askForRating(), from: fromAddress(), ...messageKind(), ...alsoPayload() });
    $("[data-preview-from]").textContent = preview.from.name ? `${preview.from.name} <${preview.from.address}>` : preview.from.address;
    $("[data-preview-to]").textContent = preview.to.name ? `${preview.to.name} <${preview.to.address}>` : preview.to.address;
    $("[data-preview-subject]").textContent = preview.subject;
    const also = alsoSelected();
    const alsoLine = $("[data-preview-also]");
    if (alsoLine) {
      alsoLine.textContent = also.length ? `Also moves to Waiting, with nothing sent to them: ${also.map((t) => t.subject || "(no subject)").join(", ")}` : "";
      alsoLine.hidden = !also.length;
    }
    const host = $("[data-preview-frame]");
    host.innerHTML = "";
    const frame = document.createElement("iframe");
    frame.setAttribute("sandbox", "");
    frame.setAttribute("title", "The reply as it will be sent");
    frame.className = "h-80 w-full rounded-md bg-white outline-1 outline-gray-200 dark:outline-white/10";
    frame.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'"><style>body{margin:12px;background:#fff}</style></head><body>${preview.html}</body></html>`;
    host.appendChild(frame);
    $("[data-composer-edit]").hidden = true;
    $("[data-composer-preview-pane]").hidden = false;
    // The preview shows this sender: a change now would send from another address than the one shown.
    $("[data-composer-from]").disabled = true;
    $("[data-composer-send]").focus();
  } catch (err) {
    ctx.showToast?.(`Couldn't build the preview: ${err.message}`, "error");
  } finally {
    if (btn) btn.disabled = false;
  }
}

function resetCountdown() {
  $("[data-composer-countdown]").textContent = "";
  $("[data-composer-send]").disabled = false;
  const back = $("[data-composer-back]");
  back.disabled = false;
  back.textContent = "Back to editing";
}

function backToEditing() {
  if (sendTimer) {
    clearInterval(sendTimer);
    sendTimer = null;
    ctx.showToast?.("Not sent.", "info");
  }
  $("[data-composer-countdown]").textContent = "";
  $("[data-composer-send]").disabled = false;
  // Re-enabled here too: sendNow disables it while the request runs, and a disabled Undo on the next send would
  // let that send go out (seen on 2026-10-08).
  $("[data-composer-back]").disabled = false;
  $("[data-composer-back]").textContent = "Back to editing";
  $("[data-composer-preview-pane]").hidden = true;
  $("[data-composer-edit]").hidden = false;
  const from = $("[data-composer-from]");
  if (from) from.disabled = false;
  if (storeMode) $("[data-store-text]")?.focus();
  else editor?.commands.focus();
}

/** Send after a short countdown; "Back to editing" during it is Undo. */
function startSend() {
  startCountdown(sendNow);
}

function startCountdown(action) {
  if (sendTimer) return;
  let left = UNDO_SECONDS;
  const send = $("[data-composer-send]");
  const back = $("[data-composer-back]");
  const countdown = $("[data-composer-countdown]");
  send.disabled = true;
  back.disabled = false;
  back.textContent = "Undo";
  countdown.textContent = `Sending in ${left}…`;
  sendTimer = setInterval(() => {
    left -= 1;
    if (left > 0) {
      countdown.textContent = `Sending in ${left}…`;
      return;
    }
    clearInterval(sendTimer);
    sendTimer = null;
    back.disabled = true;
    countdown.textContent = "Sending…";
    action();
  }, 1000);
}

/** " 2 other conversations moved to Waiting." for a send that also answered some; "" for none. */
function alsoMoved(res) {
  const n = res && Array.isArray(res.also_answered) ? res.also_answered.length : 0;
  return n ? ` ${n} other conversation${n === 1 ? "" : "s"} moved to Waiting.` : "";
}

async function sendNow() {
  const id = threadId;
  const html = currentHtml();
  const to = thread.customer.email;
  try {
    const res = await call("sendInboxReply", { threadId: id, html, askForRating: askForRating(), from: fromAddress(), requestId: crypto.randomUUID(), ...messageKind(), ...alsoPayload() });
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    lastSavedHtml = "";
    if (res.sink) ctx.showToast?.("Local emulators: the reply was recorded, not sent.", "info");
    else if (!res.appended) ctx.showToast?.(`Sent to ${to}, but the copy didn't reach the Sent folder.`, "warning");
    else ctx.showToast?.(`Sent to ${to}.${alsoMoved(res)}`, "success");
    if (threadId === id) {
      editor.commands.clearContent();
      backToEditing();
      setStatus("");
      $("[data-composer-notice]").hidden = true;
      // The next one starts as a reply again, with no subject and no tick left over.
      alsoTicked = new Set();
      drawAlso();
      const subject = $("[data-composer-subject]");
      if (subject) subject.value = "";
      setKind(false);
    }
    onSent?.(id);
  } catch (err) {
    ctx.showToast?.(`Not sent: ${err.message}`, "error");
    if (threadId === id) resetCountdown();
  }
}

// --- Store reviews (#1531): a public, plain-text reply, published by replyToStoreReview ---------------------------

const STORE_LIMITS = { app_store: 5970, google_play: 350 };
const STORE_NAMES = { app_store: "the App Store", google_play: "Google Play" };
let storeMode = false;

/** "123 / 350", turning red past the limit. */
export function storeCounterText(text, store) {
  const limit = STORE_LIMITS[store] || 0;
  const n = String(text || "").trim().length;
  return { text: `${n} / ${limit}`, over: n > limit, empty: n === 0 };
}

function mountStoreComposer(container) {
  storeMode = true;
  const store = thread.source;
  const who = thread.customer && thread.customer.name ? thread.customer.name : "the reviewer";
  container.innerHTML = `<div data-composer>
    <div class="flex flex-wrap items-baseline justify-between gap-2">
      <p class="text-sm font-semibold text-gray-900 dark:text-white">Public reply on ${STORE_NAMES[store]} <span class="font-normal text-gray-600 dark:text-gray-300">to ${escapeHTML(who)}</span></p>
      <p data-store-counter class="text-xs text-gray-500 dark:text-gray-400"></p>
    </div>
    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Everyone can read it under the review. Plain text only; a new reply replaces the earlier one.</p>
    <div data-composer-edit>
      <textarea data-store-text rows="4" aria-label="Your public reply" class="mt-2 block w-full rounded-md bg-white px-3 py-2 text-sm/6 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:outline-paprika-600 dark:bg-gray-900/40 dark:text-gray-100 dark:outline-white/10"></textarea>
      <div class="mt-2 flex justify-end"><button type="button" data-composer-preview class="${PRIMARY}">Preview &amp; publish</button></div>
    </div>
    <div data-composer-preview-pane hidden>
      <p class="mt-2 text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">How it will read on ${STORE_NAMES[store]}</p>
      <div data-store-preview class="mt-1 rounded-md bg-white px-3 py-2 text-sm/6 whitespace-pre-wrap text-gray-900 outline-1 outline-gray-200 dark:bg-white/5 dark:text-gray-100 dark:outline-white/10"></div>
      <div class="mt-3 flex items-center justify-end gap-2">
        <span data-composer-countdown class="mr-auto text-sm font-medium text-gray-700 dark:text-gray-200"></span>
        <button type="button" data-composer-back class="${SMALL}">Back to editing</button>
        <button type="button" data-composer-send class="${PRIMARY}">Publish</button>
      </div>
    </div>
  </div>`;
  const textarea = $("[data-store-text]");
  const counter = $("[data-store-counter]");
  const update = () => {
    const c = storeCounterText(textarea.value, store);
    counter.textContent = c.text;
    counter.className = c.over ? "text-xs font-semibold text-red-600" : "text-xs text-gray-500 dark:text-gray-400";
  };
  textarea.addEventListener("input", update);
  update();
  $("[data-composer-preview]").addEventListener("click", () => {
    const c = storeCounterText(textarea.value, store);
    if (c.empty) { ctx.showToast?.("Write the reply first.", "warning"); return; }
    if (c.over) { ctx.showToast?.(`${STORE_NAMES[store]} takes at most ${STORE_LIMITS[store]} characters.`, "warning"); return; }
    $("[data-store-preview]").textContent = textarea.value.trim();
    $("[data-composer-edit]").hidden = true;
    $("[data-composer-preview-pane]").hidden = false;
    $("[data-composer-send]").focus();
  });
  $("[data-composer-back]").addEventListener("click", backToEditing);
  $("[data-composer-send]").addEventListener("click", () => startCountdown(publishStoreReply));
}

async function publishStoreReply() {
  const id = threadId;
  const text = $("[data-store-text]").value.trim();
  try {
    const res = await call("replyToStoreReview", { threadId: id, text, requestId: crypto.randomUUID() });
    ctx.showToast?.(res.state === "PENDING_PUBLISH" ? "Sent to Apple: it shows once they publish it." : "Published.", "success");
    if (threadId === id) {
      $("[data-store-text]").value = "";
      backToEditing();
    }
    onSent?.(id);
  } catch (err) {
    ctx.showToast?.(`Not published: ${err.message}`, "error");
    if (threadId === id) resetCountdown();
  }
}
